import moment from "moment";

import {
  Bank,
  BankCreditNoteRequest,
  BankCreditRequestChildResponse,
  BankCreditResponse,
  NoteType,
} from "types";

export const AUTOMATIC_BANK_CREDIT_NOTE_BANKS: Bank[] = ["BANK_NOW", "CEMBRA_MONEY_AG", "CORNER"];

type AutomaticBankCreditNote = BankCreditNoteRequest & {
  creationType: "AUTOMATIC";
  code: string;
};

type AutomaticBankCreditNoteRule = {
  code: string;
  banks: Bank[];
  noteType: NoteType;
  isActive: (credit: BankCreditResponse, bank: Bank) => boolean;
  getNote: (credit: BankCreditResponse, bank: Bank) => string;
};

const normalize = (value?: string) => (value ?? "").trim().toUpperCase();

const isOneOf = (value: string | undefined, expected: string[]) =>
  expected.includes(normalize(value));

const hasPositiveAmount = (value?: number) => typeof value === "number" && value > 0;

const formatAmount = (value?: number) => `${Math.round(value ?? 0).toLocaleString("de-CH")} CHF`;

const formatDate = (value?: Date) => (value ? moment(value).format("DD.MM.YYYY") : "");

const monthsSince = (value?: Date) => {
  if (!value || !moment(value).isValid()) {
    return undefined;
  }
  return moment().diff(moment(value), "months");
};

const yearsSince = (value?: Date) => {
  if (!value || !moment(value).isValid()) {
    return undefined;
  }
  return moment().diff(moment(value), "years");
};

const getAge = (birthDate?: Date) => yearsSince(birthDate);

const getMainEmployments = (credit: BankCreditResponse) =>
  credit.employments?.filter((employment) => !employment.partner) ?? [];

const getPrimaryEmployment = (credit: BankCreditResponse) => getMainEmployments(credit)[0];

const getPrimaryEmploymentMonths = (credit: BankCreditResponse) =>
  monthsSince(getPrimaryEmployment(credit)?.contractStartDate);

const getPartnerEmployments = (credit: BankCreditResponse) =>
  credit.employments?.filter((employment) => employment.partner) ?? [];

const getMainIncomes = (credit: BankCreditResponse) =>
  credit.incomes?.filter((income) => !income.partner) ?? [];

const hasSuvaAdditionalIncome = (credit: BankCreditResponse) =>
  getMainIncomes(credit).some((income) => income.incomeType === "PENSIONSKASSE_SUVA");

const getMainSalary = (credit: BankCreditResponse) =>
  getMainEmployments(credit).reduce((sum, employment) => sum + (employment.salary ?? 0), 0);

const getPartnerSalary = (credit: BankCreditResponse) =>
  getPartnerEmployments(credit).reduce((sum, employment) => sum + (employment.salary ?? 0), 0);

const hasMainEmployment = (
  credit: BankCreditResponse,
  predicate: (employment: NonNullable<BankCreditResponse["employments"]>[number]) => boolean,
) => getMainEmployments(credit).some(predicate);

const hasMainEmploymentShorterThan = (
  credit: BankCreditResponse,
  professionalSituations: string[],
  monthsLimit: number,
) =>
  hasMainEmployment(credit, (employment) => {
    if (!professionalSituations.includes(employment.professionalSituation)) {
      return false;
    }

    const months = monthsSince(employment.contractStartDate);
    return months !== undefined && months < monthsLimit;
  });

const getAlimonyIncomeAmount = (credit: BankCreditResponse) =>
  getMainIncomes(credit)
    .filter((income) => income.incomeType === "ALIMONY")
    .reduce((sum, income) => sum + (income.amount ?? 0), 0);

const getRentIncomeAmount = (credit: BankCreditResponse) =>
  getMainIncomes(credit)
    .filter((income) => income.incomeType === "RENT")
    .reduce((sum, income) => sum + (income.amount ?? 0), 0);

const getMonthlyAlimonyExpenseByBank = (credit: BankCreditResponse, bank: Bank) => {
  if (bank === "BANK_NOW") {
    return credit.monthlyAlimonyExpensesBn;
  }
  if (bank === "CEMBRA_MONEY_AG") {
    return credit.monthlyAlimonyExpensesCb;
  }
  if (bank === "CORNER") {
    return credit.monthlyAlimonyExpensesCc;
  }
  return undefined;
};

const getCembraCalculatedAlimonyMax = (credit: BankCreditResponse) =>
  (credit.children ?? []).reduce((sum, child) => {
    const age = yearsSince(child.birthdate);
    if (age === undefined) {
      return sum;
    }
    return sum + (age < 10 ? 400 : 600);
  }, 0);

const childrenCaretakerLabels: Record<string, string> = {
  PARENTS: "die Eltern",
  GRANDMOTHER: "die Grossmutter",
  GRANDFATHER: "der Grossvater",
  BROTHER: "der Bruder",
  SISTER: "die Schwester",
  THIRD_PERSON: "eine Drittperson",
};

const getSharedHousingPeople = (credit: BankCreditResponse) =>
  (credit.housingExpenses ?? [])
    .map((housingExpense) => housingExpense.costSharingPerson?.trim())
    .filter((person): person is string => Boolean(person));

const isChildLivingWithClient = (
  credit: BankCreditResponse,
  child: BankCreditRequestChildResponse,
) =>
  credit.childrenLiveWithYou === "YES" ||
  (credit.childrenLiveWithYou === "PARTIALLY" && child.address === "WITH_CLIENT");

const getSharedAdultChildren = (credit: BankCreditResponse) => {
  const sharedPeople = new Set(getSharedHousingPeople(credit).map(normalize));

  return (credit.children ?? []).filter((child) => {
    const age = yearsSince(child.birthdate);
    const fullName = [child.firstName, child.lastName].filter(Boolean).join(" ");

    return (
      age !== undefined &&
      age >= 18 &&
      isChildLivingWithClient(credit, child) &&
      Boolean(fullName) &&
      sharedPeople.has(normalize(fullName))
    );
  });
};

const getWorkingSharedAdultChildren = (credit: BankCreditResponse) =>
  getSharedAdultChildren(credit).filter((child) => hasPositiveAmount(child.craftAmount));

const hasAgencyEmploymentShorterThanOneYear = (credit: BankCreditResponse) =>
  hasMainEmploymentShorterThan(credit, ["AGENCY"], 12);

const hasMainProfessionalSituation = (
  credit: BankCreditResponse,
  professionalSituation: "AGENCY" | "PENSION" | "DISABILITY_PENSION",
) =>
  getMainEmployments(credit).some(
    (employment) => employment.professionalSituation === professionalSituation,
  );

const getBankNowAhvMaximumRepaymentMonths = (credit: BankCreditResponse) => {
  if (getPrimaryEmployment(credit)?.professionalSituation !== "PENSION") {
    return undefined;
  }

  if (!credit.birthDate) {
    return undefined;
  }

  return moment(credit.birthDate).add(70, "years").diff(moment(), "months");
};

const hasPrimaryProfessionalSituation = (
  credit: BankCreditResponse,
  professionalSituation: "PENSION" | "DISABILITY_PENSION",
) => getPrimaryEmployment(credit)?.professionalSituation === professionalSituation;

const hasBVisaShorterThan = (credit: BankCreditResponse, monthsLimit: number) => {
  const months = monthsSince(credit.visaFrom);
  return credit.visaType === "B" && months !== undefined && months < monthsLimit;
};

const hasHourlySalaryStructureShorterThanThreeMonths = (credit: BankCreditResponse) =>
  getPrimaryEmployment(credit)?.salaryStructure === "HOURLY" &&
  (getPrimaryEmploymentMonths(credit) ?? Infinity) < 3;

const hasPrimaryEmploymentShorterThanThreeMonths = (credit: BankCreditResponse) =>
  (getPrimaryEmploymentMonths(credit) ?? Infinity) < 3;

const hasPrimaryHourlySalaryStructure = (credit: BankCreditResponse) =>
  getPrimaryEmployment(credit)?.salaryStructure === "HOURLY";

const doesPrimaryEmploymentMeetCembraPayslipRequirement = (credit: BankCreditResponse) => {
  const primaryEmployment = getPrimaryEmployment(credit);
  const months = getPrimaryEmploymentMonths(credit);

  if (!primaryEmployment || months === undefined || months >= 3) {
    return true;
  }

  return months >= 2 && primaryEmployment.salaryStructure === "FIXED";
};

const hasThirteenSalaries = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => employment.numOfSalariesPerYear === 13);

const hasBonusType = (credit: BankCreditResponse, bonusType: "ANNUAL" | "MONTHLY" | "QUARTERLY") =>
  getMainEmployments(credit).some(
    (employment) => employment.bonusReceived && employment.bonusType === bonusType,
  );

const hasSickLeaveOnPaylist = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => employment.sickLeaveOnPaylist);

const getSickLeaveReturnDate = (credit: BankCreditResponse) =>
  getMainEmployments(credit).find(
    (employment) =>
      employment.sickLeaveOnPaylist &&
      employment.sickLeaveReturnDate &&
      moment(employment.sickLeaveReturnDate).isValid(),
  )?.sickLeaveReturnDate;

const hasChildAllowanceMismatch = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => employment.childAllowanceMatches === false);

const hasEmployerLoan = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => hasPositiveAmount(employment.employerLoanAmount));

const getEmployerLoanEmployment = (credit: BankCreditResponse) =>
  getMainEmployments(credit).find((employment) => hasPositiveAmount(employment.employerLoanAmount));

const hasInvisibleIbanOnPaylist = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => employment.ibanOnPaylist === false);

const hasLongTravelDistance = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => (employment.travelingDistance ?? 0) > 10);

const neighboringNationalities = [
  "ITALY",
  "ITALIEN",
  "ITALIA",
  "AUSTRIA",
  "OESTERREICH",
  "ÖSTERREICH",
  "GERMANY",
  "DEUTSCHLAND",
  "FRANCE",
  "FRANKREICH",
  "LIECHTENSTEIN",
];

// UN M49 Europe, plus the country values stored separately by the application for UK territories.
const europeanNationalities = [
  "ALBANIA",
  "ANDORRA",
  "AUSTRIA",
  "BELARUS",
  "BELGIUM",
  "BOSNIA_HERZEGOVINA",
  "BULGARIA",
  "CROATIA",
  "CZECH_REPUBLIC",
  "DENMARK",
  "ENGLAND",
  "ESTONIA",
  "EUROPEAN_UNION_NOT_LISTED",
  "FAROE_ISLANDS",
  "FINLAND",
  "FRANCE",
  "GERMANY",
  "GIBRALTAR",
  "GREECE",
  "GREENLAND",
  "GUERNSEY",
  "HUNGARY",
  "ICELAND",
  "IRELAND",
  "ISLE_OF_MAN",
  "ITALY",
  "JERSEY_BAILIWICK_OF",
  "KOSOVO",
  "LATVIA",
  "LIECHTENSTEIN",
  "LITHUANIA",
  "LUXEMBOURG",
  "MACEDONIA",
  "MALTA",
  "MOLDOVA",
  "MONACO",
  "MONTENEGRO",
  "NORTHERN_IRELAND",
  "NORWAY",
  "POLAND",
  "PORTUGAL",
  "ROMANIA",
  "RUSSIA",
  "SAN_MARINO",
  "SCOTLAND",
  "SERBIA",
  "SLOVAK_REPUBLIC",
  "SLOVENIA",
  "SPAIN",
  "SVALBARD_AND_JAN_MAYEN_ISLANDS",
  "SWEDEN",
  "SWITZERLAND",
  "UKRAINE",
  "UNITED_KINGDOM",
  "UNITED_KINGDOM_OF_GREAT_BRITAIN_AND",
  "VATICAN_CITY",
];

const isEuropeanNationality = (nationality?: string) => isOneOf(nationality, europeanNationalities);

const isNonEuropeanNationality = (nationality?: string) =>
  Boolean(nationality?.trim()) && !isEuropeanNationality(nationality);

const refugeeStatusNationalities = ["IRAN", "IRAK", "AFGHANISTAN"];

const rules: AutomaticBankCreditNoteRule[] = [
  {
    code: "creditAmount.bankNow.minimum",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) => hasPositiveAmount(credit.creditAmount) && credit.creditAmount! < 10000,
    getNote: () => "Nicht an Bank Now senden: Mindestbetrag beträgt 10'000 CHF.",
  },
  {
    code: "repaymentPeriod.bankNow.agency.maximum",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      hasMainProfessionalSituation(credit, "AGENCY") && (credit.paymentPeriod ?? 0) > 36,
    getNote: () => "Nicht an Bank Now senden: Für Temporär ist die maximale Laufzeit 36 Monate.",
  },
  {
    code: "repaymentPeriod.bankNow.ahv.until70",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) => {
      const maximum = getBankNowAhvMaximumRepaymentMonths(credit);
      return maximum !== undefined && maximum >= 0 && (credit.paymentPeriod ?? 0) > maximum;
    },
    getNote: (credit) =>
      `Nicht an Bank Now senden: Die Laufzeit darf für AHV maximal bis zum 70. Lebensjahr dauern (${getBankNowAhvMaximumRepaymentMonths(credit)} Monate).`,
  },
  {
    code: "age.bankNow.maximumCreditAmount",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      (getAge(credit.birthDate) ?? Infinity) <= 23 && (credit.creditAmount ?? 0) > 50000,
    getNote: () =>
      "Nicht an Bank Now senden: Bis 23 Jahre beträgt der maximale Kreditbetrag 50'000 CHF.",
  },
  {
    code: "age.corner.maximumCreditAmount",
    banks: ["CORNER"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      (getAge(credit.birthDate) ?? Infinity) <= 25 && (credit.creditAmount ?? 0) > 25000,
    getNote: () =>
      "Nicht an Corner senden: Bis 25 Jahre beträgt der maximale Kreditbetrag 25'000 CHF.",
  },
  {
    code: "age.bankNowCorner.minimum",
    banks: ["BANK_NOW", "CORNER"],
    noteType: "PROCESSING",
    isActive: (credit) => {
      const age = getAge(credit.birthDate);
      return age !== undefined && age < 20;
    },
    getNote: () => "Nicht an diese Bank senden: Mindestalter beträgt 20 Jahre.",
  },
  {
    code: "age.cembra.minimum",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => {
      const age = getAge(credit.birthDate);
      return age !== undefined && age < 18;
    },
    getNote: () => "Nicht an Cembra senden: Mindestalter beträgt 18 Jahre.",
  },
  {
    code: "creditInsurance.bankNow.ineligibleProfessionalSituation",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      credit.creditInsurance === true &&
      hasMainEmployment(credit, (employment) =>
        ["DISABILITY_PENSION", "PENSION", "SELF_EMPLOYED_LUMP_SUM", "SELF_EMPLOYED_AG"].includes(
          employment.professionalSituation,
        ),
      ),
    getNote: () =>
      "Nicht an Bank Now senden: Bei IV, AHV oder Selbständigkeit ist eine Kreditversicherung nicht möglich.",
  },
  {
    code: "creditType.separate",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit) => credit.creditType === "SEPARATE",
    getNote: () => "Kunde wünscht separaten Kredit.",
  },
  {
    code: "creditPurpose.over80000",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => (credit.creditAmount ?? 0) > 80000,
    getNote: () =>
      "Der gewünschte Betrag liegt über 80.000 CHF. Bitte prüfen Sie den Verwendungszweck des Darlehens.",
  },
  {
    code: "productBankNow.casaOwnershipTooShort",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) => {
      const livingMonths = monthsSince(credit.livingFrom);
      return (
        credit.mortgage === true &&
        credit.productBn === "CLASSIC" &&
        livingMonths !== undefined &&
        livingMonths <= 6
      );
    },
    getNote: () => "Kunde ist nicht Eigentümer mehr als 6 Monate, daher kein Casa Kredit.",
  },
  {
    code: "housing.bankNow.rentDetails",
    banks: ["BANK_NOW"],
    noteType: "BANK",
    isActive: (credit) => credit.rent === true,
    getNote: () =>
      "Alle Budgetpositionen gemäss Vorgaben abgefragt und gemäss Kundenangabe erfasst / Aktuelle Wohnkosten.",
  },
  {
    code: "housing.bankNow.mortgageDetails",
    banks: ["BANK_NOW"],
    noteType: "BANK",
    isActive: (credit) => {
      const livingMonths = monthsSince(credit.livingFrom);
      return credit.mortgage === true && livingMonths !== undefined && livingMonths > 6;
    },
    getNote: () =>
      "Alle Budgetpositionen gemäss Vorgaben abgefragt und gemäss Kundenangabe erfasst / Gesamter Hypothekarbetrag.",
  },
  {
    code: "refinanceLease.selected",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => credit.refinanceLease === true,
    getNote: () => "Kunde möchte Leasing ablösen.",
  },
  {
    code: "fullName.cembra.tooLong",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "BANK",
    isActive: (credit) => (credit.user?.fullName?.trim().length ?? 0) > 29,
    getNote: (credit) => `Voller Name des Kundens: ${credit.user.fullName}`,
  },
  {
    code: "nationality.neighboringCountry.borderWorkerCheck",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => isOneOf(credit.nationality, neighboringNationalities),
    getNote: () => "Prüfen ob der Kunde Grenzgänger war.",
  },
  {
    code: "nationality.romania.bVisa.bankNow",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      isOneOf(credit.nationality, ["ROMANIA", "RUMÄNIEN", "RUMAENIEN"]) && credit.visaType === "B",
    getNote: () => "Nicht an Bank Now senden: Rumänen mit B-Bewilligung sind nicht zugelassen.",
  },
  {
    code: "nationality.bulgaria.bVisa.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      isOneOf(credit.nationality, ["BULGARIA", "BULGARIEN"]) && credit.visaType === "B",
    getNote: () => "Nicht an Cembra senden: Bulgaren mit B-Bewilligung sind nicht zugelassen.",
  },
  {
    code: "visa.bankNow.minimumTwoYears",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      hasBVisaShorterThan(credit, 24) && isEuropeanNationality(credit.nationality),
    getNote: () => "Nicht an Bank Now senden: B-Bewilligung besteht weniger als 2 Jahre.",
  },
  {
    code: "visa.bankNow.nonEuropean.minimumThreeYears",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      hasBVisaShorterThan(credit, 36) && isNonEuropeanNationality(credit.nationality),
    getNote: () =>
      "Nicht an Bank Now senden: Für Staatsangehörige ausserhalb Europas muss die B-Bewilligung mindestens 3 Jahre bestehen.",
  },
  {
    code: "visa.cembra.minimumEighteenMonths",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => hasBVisaShorterThan(credit, 18),
    getNote: () => "Nicht an Cembra senden: B-Bewilligung besteht weniger als 18 Monate.",
  },
  {
    code: "nationality.refugeeStatus.check",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => isOneOf(credit.nationality, refugeeStatusNationalities),
    getNote: () => "Prüfen ob der Kunde einen Flüchtlingsstatus hat.",
  },
  {
    code: "children.abroad",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit) => credit.childrenInAbroad === true,
    getNote: () => "Kind/er im Ausland.",
  },
  {
    code: "children.foreignTransferAmount",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit) =>
      credit.childrenInAbroad === true && hasPositiveAmount(credit.foreignTransferAmount),
    getNote: (credit) => `Kunde sendet ins Ausland ${formatAmount(credit.foreignTransferAmount)}.`,
  },
  {
    code: "children.foreignTransferAmount.cembraMinimum",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "BANK",
    isActive: (credit) =>
      credit.childrenInAbroad === true && hasPositiveAmount(credit.foreignTransferAmount),
    getNote: () => "Cembra: Mindestbetrag für Auslagen im Ausland sind die Kinderzulagen.",
  },
  {
    code: "income.alimony",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit) => hasPositiveAmount(getAlimonyIncomeAmount(credit)),
    getNote: (credit) => {
      const customerLabel = credit.gender === "FEMALE" ? "Kundin" : "Kunde";
      return `${customerLabel} bekommt ${formatAmount(getAlimonyIncomeAmount(credit))} Alimente.`;
    },
  },
  {
    code: "alimony.expense",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit, bank) =>
      credit.paysAlimony === true &&
      hasPositiveAmount(getMonthlyAlimonyExpenseByBank(credit, bank)),
    getNote: (credit, bank) =>
      `Kunde zahlt Alimente ${formatAmount(getMonthlyAlimonyExpenseByBank(credit, bank))}.`,
  },
  {
    code: "children.craft.cembra.rentReduction",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => (credit.children ?? []).some((child) => child.craft),
    getNote: () => "Cembra: Miete wurde -250CHF reduziert wegen Lehrlingslohn.",
  },
  {
    code: "alimony.cembra.maxCalculated",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "BANK",
    isActive: (credit) => {
      const calculatedMax = getCembraCalculatedAlimonyMax(credit);
      return calculatedMax > 0 && (credit.monthlyAlimonyExpensesCb ?? 0) > calculatedMax;
    },
    getNote: (credit) =>
      `Cembra: Alimente werden max ${formatAmount(getCembraCalculatedAlimonyMax(credit))} berechnet.`,
  },
  {
    code: "alimony.duration.check",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => credit.paysAlimony === true && (credit.children?.length ?? 0) > 0,
    getNote: () => "Alimentendauer beachten! Alter des Kindes prüfen.",
  },
  {
    code: "children.caretaker.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "BANK",
    isActive: (credit) => !!credit.childrenCaretaker,
    getNote: (credit) => {
      const caretaker = childrenCaretakerLabels[credit.childrenCaretaker ?? ""] ?? "eine Person";
      return `[CB] ${caretaker} passt auf das Kind auf.`;
    },
  },
  {
    code: "partner.abroad",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => credit.partnerResidence === "ABROAD",
    getNote: () => "Ehepartner lebt im Ausland.",
  },
  {
    code: "professional.selfEmployed.cembra.minimumEighteenMonths",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      hasMainEmploymentShorterThan(credit, ["SELF_EMPLOYED_LUMP_SUM", "SELF_EMPLOYED_AG"], 18),
    getNote: () => "Nicht an Cembra senden: Selbständigkeit besteht weniger als 18 Monate.",
  },
  {
    code: "professional.pension.onlyBankNow",
    banks: ["CEMBRA_MONEY_AG", "CORNER"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      ["DISABILITY_PENSION", "PENSION"].includes(
        getPrimaryEmployment(credit)?.professionalSituation ?? "",
      ),
    getNote: () =>
      "Nicht an diese Bank senden: IV-/AHV-Rente als Haupterwerb kann nur an Bank Now eingereicht werden.",
  },
  {
    code: "repaymentPeriod.bankNow.iv.withoutSuva",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      hasPrimaryProfessionalSituation(credit, "DISABILITY_PENSION") &&
      !hasSuvaAdditionalIncome(credit) &&
      credit.paymentPeriod !== undefined &&
      credit.paymentPeriod !== 36,
    getNote: () =>
      "Nicht an Bank Now senden: Bei IV-Rente ohne SUVA sind nur 36 Monate Laufzeit möglich.",
  },
  {
    code: "repaymentPeriod.bankNow.iv.withSuva",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      hasPrimaryProfessionalSituation(credit, "DISABILITY_PENSION") &&
      hasSuvaAdditionalIncome(credit) &&
      credit.paymentPeriod !== undefined &&
      ![36, 48].includes(credit.paymentPeriod),
    getNote: () =>
      "Nicht an Bank Now senden: Bei IV-Rente mit SUVA sind nur 36 oder 48 Monate Laufzeit möglich.",
  },
  {
    code: "professional.selfEmployed.bVisaUnder25.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      credit.visaType === "B" &&
      (getAge(credit.birthDate) ?? Infinity) < 25 &&
      hasMainEmployment(credit, (employment) =>
        ["SELF_EMPLOYED_LUMP_SUM", "SELF_EMPLOYED_AG"].includes(employment.professionalSituation),
      ),
    getNote: () =>
      "Nicht an Cembra senden: Selbständige Kunden unter 25 Jahren mit B-Bewilligung sind nicht zugelassen.",
  },
  {
    code: "professional.selfEmployed.bankNow.minimumTwoYears",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      hasMainEmploymentShorterThan(credit, ["SELF_EMPLOYED_LUMP_SUM", "SELF_EMPLOYED_AG"], 24),
    getNote: () => "Nicht an Bank Now senden: Selbständigkeit besteht weniger als 2 Jahre.",
  },
  {
    code: "professional.selfEmployed.corner.minimumThreeYears",
    banks: ["CORNER"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      hasMainEmploymentShorterThan(credit, ["SELF_EMPLOYED_LUMP_SUM", "SELF_EMPLOYED_AG"], 36),
    getNote: () => "Nicht an Corner senden: Selbständigkeit besteht weniger als 3 Jahre.",
  },
  {
    code: "professional.agencyShortBankNow",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: hasAgencyEmploymentShorterThanOneYear,
    getNote: () => "Nicht an Bank Now senden: Temporär-Anstellung besteht weniger als 1 Jahr.",
  },
  {
    code: "professional.agency.allBanks.minimumSixMonths",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => hasMainEmploymentShorterThan(credit, ["AGENCY"], 6),
    getNote: () => "Nicht an diese Bank senden: Temporär-Anstellung besteht weniger als 6 Monate.",
  },
  {
    code: "employment.defined.allBanks",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) =>
      hasMainEmployment(credit, (employment) => employment.employmentType === "DEFINED"),
    getNote: () =>
      "Nicht an diese Bank senden: Ein Arbeitsvertrag auf bestimmte Dauer ist nicht zulässig.",
  },
  {
    code: "salaryStructure.hourly",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: hasHourlySalaryStructureShorterThanThreeMonths,
    getNote: () =>
      "Nicht an diese Bank senden: Bei Stundenlohn müssen mindestens drei Lohnabrechnungen vorliegen.",
  },
  {
    code: "employment.corner.minimumThreeMonths",
    banks: ["CORNER"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      !hasPrimaryHourlySalaryStructure(credit) &&
      hasPrimaryEmploymentShorterThanThreeMonths(credit),
    getNote: () =>
      "Nicht an Corner senden: Beim aktuellen Arbeitgeber müssen mindestens drei Lohnabrechnungen vorliegen.",
  },
  {
    code: "employment.cembra.payslipRequirement",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      !hasPrimaryHourlySalaryStructure(credit) &&
      !doesPrimaryEmploymentMeetCembraPayslipRequirement(credit),
    getNote: (credit) => {
      const months = getPrimaryEmploymentMonths(credit);
      return months !== undefined && months < 2
        ? "Nicht an Cembra senden: Beim aktuellen Arbeitgeber müssen mindestens zwei Lohnabrechnungen vorliegen."
        : "Nicht an Cembra senden: Im begonnenen dritten Monat reichen zwei Lohnabrechnungen nur bei festem Lohn aus.";
    },
  },
  {
    code: "income.net.cornerBelow3500",
    banks: ["CORNER"],
    noteType: "PROCESSING",
    isActive: (credit) => {
      const salary = getMainSalary(credit);
      return salary > 0 && salary < 3500;
    },
    getNote: () => "Nicht an Corner senden: Monatliches Nettoeinkommen liegt unter 3'500 CHF.",
  },
  {
    code: "income.net.bankNowBelow2800",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) => {
      const salary = getMainSalary(credit);
      return salary > 0 && salary < 2800;
    },
    getNote: () => "Nicht an Bank Now senden: Monatliches Nettoeinkommen liegt unter 2'800 CHF.",
  },
  {
    code: "income.partner.cornerBelow1000",
    banks: ["CORNER"],
    noteType: "PROCESSING",
    isActive: (credit) => {
      const salary = getPartnerSalary(credit);
      return salary > 0 && salary < 1000;
    },
    getNote: () =>
      "Nicht an Corner senden: Das Einkommen des zweiten Antragstellers liegt unter 1'000 CHF.",
  },
  {
    code: "income.main.cembra.partnerCreditEligible",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => {
      const salary = getMainSalary(credit);
      return salary > 0 && salary < 2500;
    },
    getNote: () =>
      "Cembra: Kunde kommt wegen einem monatlichen Nettoeinkommen unter 2'500 CHF für einen Partnerkredit in Frage.",
  },
  {
    code: "salary.thirteen",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: hasThirteenSalaries,
    getNote: () => "Lohnabrechnung 13ten Lohn einverlangen. (NOV-FEB)",
  },
  {
    code: "bonus.annual.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => hasBonusType(credit, "ANNUAL"),
    getNote: () =>
      "Cembra: Jahresbonus, letzte 3 LAs mit Jahresbonus erforderlich. (der kleinste wird berechnet)",
  },
  {
    code: "bonus.quarterly.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => hasBonusType(credit, "QUARTERLY"),
    getNote: () =>
      "Cembra: Quartalbonus letzte 3 LAs mit Quartalbonus erforderlich. (durchschnitt wird gerechnet)",
  },
  {
    code: "bonus.monthly.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => hasBonusType(credit, "MONTHLY"),
    getNote: () =>
      "Cembra: Monatsbonus letzte 3 LAs mit Monatsbonus erforderlich. (durchschnitt wird gerechnet)",
  },
  {
    code: "sickLeave.confirmation",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: hasSickLeaveOnPaylist,
    getNote: () => "Bestätigung oder neue LA einverlangen.",
  },
  {
    code: "sickLeave.returnToWork",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit) => getSickLeaveReturnDate(credit) !== undefined,
    getNote: (credit) =>
      `Kunde ist wieder 100% arbeitstätig ab ${formatDate(getSickLeaveReturnDate(credit))}.`,
  },
  {
    code: "childAllowance.mismatch",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: hasChildAllowanceMismatch,
    getNote: () => "Kinderzulagen abklären!",
  },
  {
    code: "employerLoan.advance",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: hasEmployerLoan,
    getNote: (credit) => {
      const employment = getEmployerLoanEmployment(credit);
      const dateText = employment?.employerLoanPaidOut
        ? ` bis ${formatDate(employment.employerLoanPaidOut)}`
        : "";
      return `Kunde hat einen Vorschuss beim Arbeitgeber${dateText} monatlich zahlt er ${formatAmount(employment?.employerLoanAmount)} ab.`;
    },
  },
  {
    code: "distance.workplaceCheck",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: hasLongTravelDistance,
    getNote: () => "Überprüfe den Arbeitsort.",
  },
  {
    code: "transport.bankNow.details",
    banks: ["BANK_NOW"],
    noteType: "BANK",
    isActive: (credit) =>
      getMainEmployments(credit).some(
        (employment) =>
          employment.travelingToWorkBn !== undefined ||
          hasPositiveAmount(employment.travelExpensesBn),
      ),
    getNote: () =>
      "Alle Budgetpositionen gemäss Vorgaben abgefragt und gemäss Kundenangabe erfasst / Transportmittel / Aktuelle Wohnkosten.",
  },
  {
    code: "iban.notVisible",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: hasInvisibleIbanOnPaylist,
    getNote: () =>
      "IBAN nicht ersichtlich. Bankkarte einverlangen. Prüfen ob Kunde den Lohn aus Konto bekommt.",
  },
  {
    code: "income.rent.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => hasPositiveAmount(getRentIncomeAmount(credit)),
    getNote: (credit) =>
      `Cembra: Liegenschaftseinkommen werden ${formatAmount(getRentIncomeAmount(credit) * 0.6)} berechnet.`,
  },
  {
    code: "housing.additionalExpensesNotIncluded",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => credit.additionalHousingExpensesIncluded === false,
    getNote: () => "Nebenkostenabrechnung einverlangen.",
  },
  {
    code: "housing.rentSharedWithAdultChildren",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit) => getSharedAdultChildren(credit).length > 0,
    getNote: () => "Miete wurde geteilt mit erwachsenem/erwachsenen Kind/Kindern.",
  },
  {
    code: "housing.cembra.rentSharedWithWorkingAdultChildren",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "BANK",
    isActive: (credit) => getWorkingSharedAdultChildren(credit).length > 0,
    getNote: (credit) =>
      getWorkingSharedAdultChildren(credit).length === 1
        ? "„[CB] Miete wurde geteilt mit erwachsenem Kind.“"
        : "„[CB] Miete wurde geteilt mit erwachsenen Kindern.“",
  },
  {
    code: "enforcedCollection.check",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => credit.enforcedCollection === true,
    getNote: () => "Betreibung einverlangen und CRIF prüfen.",
  },
];

// These codes are no longer desired, but remain managed so existing automatic notes are deleted.
const deprecatedAutomaticBankCreditNoteCodes = [
  "housing.bankNow.rentSharedWith",
  "housing.cembra.rentSharedWithChildren",
  "professional.selfEmployedBVisa.cembra",
  "repaymentPeriod.maximum",
];

export const managedAutomaticBankCreditNoteCodes = new Set([
  ...rules.map((rule) => rule.code),
  ...deprecatedAutomaticBankCreditNoteCodes,
]);

export const buildAutomaticBankCreditNotes = (
  credit: BankCreditResponse,
): AutomaticBankCreditNote[] =>
  rules.flatMap((rule) =>
    rule.banks
      .filter((bank) => rule.isActive(credit, bank))
      .map((bank) => ({
        bankCreditRequestId: credit.id,
        bank,
        noteType: rule.noteType,
        creationType: "AUTOMATIC",
        code: rule.code,
        note: rule.getNote(credit, bank),
      })),
  );
